import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

// Section types
export type SectionType = 'prematuri' | 'chd' | null;

// Context type definition
interface SectionContextType {
  activeSection: SectionType;
  setActiveSection: (section: SectionType) => void;
  clearSection: () => void;
}

// Create the context
const SectionContext = createContext<SectionContextType | undefined>(undefined);

// Custom hook to use the section context
export const useSectionContext = (): SectionContextType => {
  const context = useContext(SectionContext);
  if (context === undefined) {
    throw new Error('useSectionContext must be used within a SectionProvider');
  }
  return context;
};

// Props for SectionProvider
interface SectionProviderProps {
  children: ReactNode;
}

// Get initial section from sessionStorage
const getInitialSection = (): SectionType => {
  if (typeof window === 'undefined') return null;
  const stored = sessionStorage.getItem('activeSection');
  if (stored === 'prematuri' || stored === 'chd') {
    return stored;
  }
  return null;
};

// SectionProvider component
export const SectionProvider: React.FC<SectionProviderProps> = ({ children }) => {
  const [activeSection, setActiveSectionState] = useState<SectionType>(getInitialSection);

  const setActiveSection = useCallback((section: SectionType) => {
    setActiveSectionState(section);
    if (section) {
      sessionStorage.setItem('activeSection', section);
    } else {
      sessionStorage.removeItem('activeSection');
    }
  }, []);

  const clearSection = useCallback(() => {
    setActiveSectionState(null);
    sessionStorage.removeItem('activeSection');
  }, []);

  // AuthContext dispatches this on logout - it can't call clearSection()
  // directly since SectionProvider is nested inside AuthProvider.
  useEffect(() => {
    window.addEventListener('clearSection', clearSection);
    return () => window.removeEventListener('clearSection', clearSection);
  }, [clearSection]);

  const contextValue: SectionContextType = {
    activeSection,
    setActiveSection,
    clearSection,
  };

  return (
    <SectionContext.Provider value={contextValue}>
      {children}
    </SectionContext.Provider>
  );
};

export default SectionContext;
